import React from "react";

import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

import VisibilityIcon from "@mui/icons-material/Visibility";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";

import { displayList } from "@auto/widgets/ListComponent";
import { rest, t } from "@auto/services";
import { getServiceUri } from "@auto/metadata";
import { can } from "@src/security/permissions";

import {
  PERSON_LIST_FACETS,
  buildPersonFilterRsql,
  combinePersonListRsql,
  isDefaultPersonListFilters
} from "./PersonListFacets";

import styles from "./PersonList.module.styl";

const PERSON_LIST_DEFAULT_PAGE_SIZE = 50;

let initialPersonListRange = null;
let initialPersonListRangeUsesLeft = 0;

const PERSON_SEARCH_FIELDS = [
  "firstName",
  "lastName",
  "nationalIdentifier",
  "officeIdentifierNumber",
  "recordNumber"
];

const fullName = (row) => {
  return `${row.firstName || ""} ${row.lastName || ""}`.trim();
};

const personIdentifier = (row) => {
  return row.nationalIdentifier || row.officeIdentifierNumber || row.recordNumber || row.gid || row.id || "";
};

const normalizeTags = (tags) => {
  if (!tags || typeof tags !== "object" || Array.isArray(tags)) {
    return {};
  }

  return tags;
};

const personImageSrc = (row) => {
  if (!row?.defaultImageId) {
    return "";
  }

  return `${getServiceUri()}person-photo/data/${Number(row.defaultImageId)}`;
};

const personCardUrl = (row) => {
  const id = row.gid || row.id;

  if (!id) {
    throw new Error("person.gid or person.id is required to build person card url");
  }

  return `/person/${id}`;
};

const escapeRsqlRegexValue = (value) => {
  return String(value || "")
    .trim()
    .replace(/\\/g, "\\\\")
    .replace(/"/g, '\\"')
    .replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
};

const buildPersonSearchQuery = (search) => {
  const value = escapeRsqlRegexValue(search);

  if (!value) {
    return "";
  }

  return PERSON_SEARCH_FIELDS
    .map((field) => `${field}=re="(?i).*${value}.*"`)
    .join(",");
};

const readPersonListRangeFromUrl = () => {
  const params = new URLSearchParams(window.location.search);

  const offset = Number(params.get("offset"));
  const limit = Number(params.get("limit"));

  const safeLimit =
    Number.isFinite(limit) && limit > 0
      ? Math.floor(limit)
      : PERSON_LIST_DEFAULT_PAGE_SIZE;

  const safeOffset =
    Number.isFinite(offset) && offset >= 0
      ? Math.floor(offset)
      : 0;

  return {
    offset: safeOffset,
    limit: safeLimit,
    page: Math.floor(safeOffset / safeLimit),
    pageSize: safeLimit,
    hasExplicitRange: params.has("offset") || params.has("limit")
  };
};

const writePersonListRangeToUrl = ({ offset, limit }) => {
  const url = new URL(window.location.href);

  url.searchParams.set("offset", String(offset));
  url.searchParams.set("limit", String(limit));

  window.history.replaceState({}, "", url.toString());
};

const getEffectivePersonListQuery = (query) => {
  const search = String(query?.search || "").trim();
  const sort = Array.isArray(query?.sort) ? query.sort : [];
  const filters = query?.filters || {};
  const hasActiveFilters = !isDefaultPersonListFilters(filters);

  const page = Number.isFinite(query?.page) ? query.page : 0;
  const pageSize = Number.isFinite(query?.pageSize)
    ? query.pageSize
    : PERSON_LIST_DEFAULT_PAGE_SIZE;

  if (
    initialPersonListRange &&
    initialPersonListRange.hasExplicitRange &&
    initialPersonListRangeUsesLeft > 0 &&
    !search &&
    !hasActiveFilters &&
    page === 0 &&
    pageSize === PERSON_LIST_DEFAULT_PAGE_SIZE
  ) {
    initialPersonListRangeUsesLeft -= 1;

    return {
      page: initialPersonListRange.page,
      pageSize: initialPersonListRange.pageSize,
      offset: initialPersonListRange.offset,
      limit: initialPersonListRange.limit,
      search,
      sort,
      filters
    };
  }

  initialPersonListRangeUsesLeft = 0;

  return {
    page,
    pageSize,
    offset: page * pageSize,
    limit: pageSize,
    search,
    sort,
    filters
  };
};

const selectorsForSortItem = (sortItem) => {
  const column = sortItem?.column;

  if (Array.isArray(column?.sortSelectors)) {
    return column.sortSelectors;
  }

  if (column?.sortSelector) {
    return [column.sortSelector];
  }

  if (sortItem?.field) {
    return [sortItem.field];
  }

  throw new Error("Invalid sort item: missing sort selector");
};

const buildSortPayload = (sort) => {
  const out = [];

  for (const sortItem of sort || []) {
    if (sortItem?.direction !== "asc" && sortItem?.direction !== "desc") {
      throw new Error(`Invalid sort direction: ${sortItem?.direction}`);
    }

    for (const selector of selectorsForSortItem(sortItem)) {
      if (!selector) {
        throw new Error(`Invalid sort selector for field: ${sortItem?.field}`);
      }

      out.push({
        selector,
        direction: sortItem.direction
      });
    }
  }

  return out;
};

const buildPersonListRsql = (range) => {
  return combinePersonListRsql(
    buildPersonSearchQuery(range.search),
    buildPersonFilterRsql(range.filters)
  );
};

const buildPersonSearchPayload = (range) => {
  return {
    columns: [],
    query: buildPersonListRsql(range),
    limit: range.limit,
    offset: range.offset,
    sort: buildSortPayload(range.sort)
  };
};

const enrichPersonRows = (persons) => {
  return (persons || []).map((person) => ({
    ...person,
    fullName: fullName(person),
    identifier: personIdentifier(person),
    hasPhoto: Boolean(person.defaultImageId)
  }));
};

const renderPicture = (row, gui) => {
  const src = can(gui, "READ_PERSON_PHOTO") ? personImageSrc(row) : "";

  if (!src) {
    return <AccountCircleIcon fontSize="large" className={styles.emptyAvatarIcon} />;
  }

  return (
    <img
      src={src}
      alt={row.fullName || fullName(row) || t("person.pictureAlt")}
      className={styles.avatar}
    />
  );
};

const renderName = (row, gui) => {
  const label = row.fullName || fullName(row) || "";

  if (!label) {
    return null;
  }

  if (!can(gui, "READ_PERSON")) {
    return <span className={styles.name}>{label}</span>;
  }

  return (
    <a
      className={styles.name}
      href={personCardUrl(row)}
      onClick={(event) => {
        event.stopPropagation();
      }}
    >
      {label}
    </a>
  );
};

const renderIdentifier = (row) => {
  if (!row.identifier) {
    return null;
  }

  return <span>{row.identifier}</span>;
};

const renderTags = (row) => {
  const tags = normalizeTags(row.tags);
  const activeTags = Object.entries(tags)
    .filter(([, enabled]) => enabled === true)
    .map(([key]) => key);

  if (!activeTags.length) {
    return null;
  }

  return (
    <div className={styles.inlineTagsInList}>
      {activeTags.map((tag) => (
        <span key={tag} className={styles.tagChip}>{tag}</span>
      ))}
    </div>
  );
};

const renderViewAction = (row, gui) => {
  if (!can(gui, "READ_PERSON")) {
    return null;
  }

  return (
    <Tooltip title={t("common.view")}>
      <IconButton
        size="small"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          gui.goTo(personCardUrl(row));
        }}
      >
        <VisibilityIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
};

const personListProjection = {
  storageKey: "haiti.person",
  facets: PERSON_LIST_FACETS,

  columns: [
    {
      title: "common.picture",
      field: "defaultImageId",
      sorting: false,
      filtering: false,
      render: renderPicture
    },

    {
      title: "common.name",
      field: "fullName",
      sortSelectors: ["lastName", "firstName"],
      render: renderName
    },

    {
      title: "common.identifier",
      field: "identifier",
      sortSelector: "nationalIdentifier",
      render: renderIdentifier
    },

    {
      title: "person.birthDate",
      field: "dateOfBirth",
      sortSelector: "dateOfBirth"
    },

    {
      title: "person.sex",
      field: "sexCode",
      sortSelector: "sexCode"
    },


    {
      title: "common.tags",
      field: "tags",
      sorting: false,
      filtering: false,
      render: renderTags
    },

    {
      title: "common.actions",
      field: "__actions",
      sorting: false,
      filtering: false
    }
  ],

  actions: renderViewAction,

  fetch: async (query) => {
    const range = getEffectivePersonListQuery(query);

    writePersonListRangeToUrl({
      offset: range.offset,
      limit: range.limit
    });

    const payload = buildPersonSearchPayload(range);

    const persons = await rest.request(
      getServiceUri() + "person/search",
      "POST",
      payload
    );

    const totalCount = await rest.request(
      getServiceUri() + "person/count",
      "POST",
      {
        columns: [],
        query: payload.query,
        limit: null,
        offset: null,
        sort: []
      }
    );

    return {
      data: enrichPersonRows(persons || []),
      totalCount,
      page: range.page
    };
  }
};

export const displayPersonList = (gui) => {
  initialPersonListRange = readPersonListRangeFromUrl();
  initialPersonListRangeUsesLeft = initialPersonListRange.hasExplicitRange ? 3 : 0;

  return displayList(gui, personListProjection);
};

export default displayPersonList;
